

























































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { GatewayContext } from "@/data/enums/gateway";
import { IInvoice } from "@/models/invoices";
import { IPaymentDetail } from "@/models/paymentDetails";
import { Location as VueLocation } from "vue-router";
import { QUERY_PARAMS } from "@/data/constants";

import HasValidation from "@/mixins/HasValidation";
import LoggedUserMixin from "@/mixins/loggedUserMixin";
import PaymentProvider from "@/components/app/global/payments/paymentProvider.vue";
import { DataModules } from "@/store/modules/data";

@Component({
  components: {
    "select-payment-method": () =>
      import("@/components/app/global/payments/selectPaymentMethodComp.vue")
  }
})
export default class InvoicePaymentModal extends Mixins(
  PaymentProvider,
  LoggedUserMixin,
  HasValidation
) {
  // Props
  @Prop({ required: true })
  public invoice!: IInvoice;

  @Prop({ default: "" })
  public preselectedStoredPaymentMethodId!: IPaymentDetail["id"];

  @Prop({ default: undefined })
  public preselectedShowPendingPayment!: boolean;

  @Prop({ default: undefined })
  public externalAuthLocationFunc!: () => VueLocation;

  // Data
  public isProcessing = false;
  public showPendingPayment = false;

  public created() {
    this.showPendingPayment = !this.$_.isNil(this.preselectedShowPendingPayment)
      ? this.preselectedShowPendingPayment
      : this.hasPendingPayment;
  }

  // Computed

  public get additionalManualPaymentData() {
    return {
      invoice_id: this.invoice.id
    };
  }

  public get selectPaymentMethodBindings() {
    return {
      accountId: this.invoice.account_id,
      autoSelectFirstStoredPaymentMethod: !this
        .preselectedStoredPaymentMethodId,
      brandId: this.invoice.brand_id,
      canChangeManualPaymentAmount: true,
      cardHolderName: this.invoice.client?.fullname,
      clientId: this.invoice.client_id,
      currencyId: this.invoice.currency_id,
      currencyCode: this.invoice.currency?.code,
      externalAuthReturnLocation: this.externalAuthReturnLocation,
      gatewayContext: GatewayContext.PAY,
      hasManualPayment: this.isAdmin,
      hasPartialPayment: this.isAdmin,
      hasPayLater: false,
      isDisabled: this.isProcessing,
      onlineGatewaysOnly: false,
      preselectedStoredPaymentMethodId: this.preselectedStoredPaymentMethodId,
      totalAmount: this.totalAmountToPay
    };
  }

  // Override
  public get externalAuthReturnLocation(): VueLocation {
    if (this.$_.isFunction(this.externalAuthLocationFunc)) {
      return this.externalAuthLocationFunc();
    }

    return this.buildResolver({
      returnLocation: this.$store.getters[
        `data/${DataModules.INVOICES}/getRoute`
      ](this.invoice.id, this.invoice.client_id),
      paymentMethodType: this.paymentMethodType,
      initPay: this.isExternalStore ? { invoiceId: this.invoice.id } : undefined
    }).location;
  }

  public get hasPendingPayment() {
    return !!this.invoice?.payments?.find(i => i.pending);
  }

  public get attemptKey() {
    return btoa(
      JSON.stringify({
        invoiceId: this.invoice.id,
        timestamp: this.$moment()
          .toDate()
          .getTime()
      })
    );
  }

  public get additionalPaymentDetailsData() {
    return {
      clientId: this.invoice.client_id,
      scaReturnLocation: this.$router.resolve({
        name: this.isAdmin ? "openAdminInvoice" : "openInvoice",
        params: {
          clientId: `${this.invoice.client_id}`,
          invoiceId: `${this.invoice.id}`
        }
      }).location
    };
  }

  public get additionalPaymentData() {
    const origin = window.location.origin;

    const returnRoute = this.$router.resolve(this.externalAuthReturnLocation)
      .route;

    const successLocation = this.$_.merge({}, returnRoute, {
      query: {
        [QUERY_PARAMS.PAYMENT_SUCCESS]: "true",
        [QUERY_PARAMS.ATTEMPT]: this.attemptKey
      }
    });

    const failLocation = this.$_.merge({}, returnRoute, {
      query: {
        [QUERY_PARAMS.PAYMENT_SUCCESS]: "false",
        [QUERY_PARAMS.ATTEMPT]: this.attemptKey
      }
    });

    const returnUrlEncodedSuccess = encodeURIComponent(
      `${origin}${successLocation.fullPath}`
    );

    const returnUrlEncodedFail = encodeURIComponent(
      `${origin}${failLocation.fullPath}`
    );

    return {
      client_id: this.invoice.client_id,
      account_id: this.invoice.account_id,
      invoice_id: this.invoice.id,
      return_url: `?${QUERY_PARAMS.SUCCESS}=${returnUrlEncodedSuccess}&${QUERY_PARAMS.FAILED}=${returnUrlEncodedFail}`,
      cancel_url: `${origin}${returnRoute.fullPath}`
    };
  }

  public get totalAmountToPay() {
    return this.invoice.unpaid_amount_converted;
  }

  // Methods

  @Watch("paymentMethod", { deep: true, immediate: true })
  public onPaymentMethodChanged() {
    if (!this.$_.isEmpty(this.paymentMethod))
      this.$emit("payment-method-selected", this.paymentMethod);
  }

  public async pay() {
    this.isProcessing = true;
    try {
      if (!(await this.validate())) throw this.$t("_.form_invalid");
      // Prepare payment data
      await this.preparePaymentData(this.invoice.client_id);

      if (this.externalAuth)
        await this.redirectToExternalAuth(this.externalAuth);

      // Execute payment (may redirect off-site for SCA)
      await this.executePayment(this.attemptKey);
      // Emit 'success'
      this.$emit("success", this.invoice);
    } catch (error) {
      this.$emit("fail", this.invoice);
      this.isProcessing = false;
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }
  }
}
